import { ComponentPropsWithoutRef, ForwardedRef, forwardRef, useContext } from 'react';
import { Button } from '@givebutter/primitives/button';
import * as Dialog from '@givebutter/primitives/dialog';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ImportStrategyContext } from 'components/imports/context';
import * as DownloadImportTemplateModal from 'components/pages/contacts/components/DownloadImportTemplateModal';
import { twMerge } from 'tailwind-merge';

const Body = forwardRef((props: Dialog.DialogBodyProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { className, ...rest } = props;

    return (
        <Dialog.Body
            ref={ref}
            className={twMerge(
                'flatfile-no-modal-container flex w-full flex-col gap-y-4 overflow-y-auto',
                className,
            )}
            {...rest}
        />
    );
});

interface ImportDialogHelpCenterProps extends ComponentPropsWithoutRef<'div'> {}

const HelpCenter = (props: ImportDialogHelpCenterProps) => {
    const { className, ...rest } = props;
    const { importStrategy } = useContext(ImportStrategyContext);

    return (
        <div className={twMerge('flex-col gap-4', className)} {...rest}>
            {(importStrategy.helpCenterUrl || importStrategy.hasDownloadTemplate) && (
                <div className={'min-w-48 max-w-64'}>
                    <h3 className={'text-sm font-medium leading-5'}>Need help getting started?</h3>
                    {importStrategy.helpCenterUrl && (
                        <a
                            href={importStrategy.helpCenterUrl!}
                            rel="noopener noreferrer"
                            target="_blank"
                            className={
                                'inline-flex items-center gap-x-2.5 text-sm font-semibold text-blue-500'
                            }
                        >
                            <span>Read our help center article</span>
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        </a>
                    )}
                    {importStrategy.hasDownloadTemplate && (
                        <DownloadImportTemplateModal.Root type={importStrategy.type}>
                            <DownloadImportTemplateModal.Trigger asChild={true}>
                                <Button
                                    variant="none"
                                    color="primary"
                                    size="sm"
                                    className="font-semibold"
                                >
                                    <span>Download Template</span>
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                </Button>
                            </DownloadImportTemplateModal.Trigger>
                        </DownloadImportTemplateModal.Root>
                    )}
                </div>
            )}
        </div>
    );
};

const Content = forwardRef(
    (props: Dialog.DialogContentProps, ref: ForwardedRef<HTMLDivElement>) => {
        const { className, variant, ...rest } = props;

        return (
            <Dialog.Content
                ref={ref}
                variant={variant ?? 'fullscreen'}
                className={twMerge('overflow-y-auto p-6', className)}
                {...rest}
            />
        );
    },
);

interface ImportDialogDraftProps extends ComponentPropsWithoutRef<'div'> {}

const Draft = (props: ImportDialogDraftProps) => {
    const { children, className, ...rest } = props;

    return (
        <div className={twMerge('flex items-center gap-x-2 text-sm', className)} {...rest}>
            {children}
        </div>
    );
};

const Header = forwardRef((props: Dialog.DialogHeaderProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { className, ...rest } = props;

    return <Dialog.Header ref={ref} className={twMerge('mb-4', className)} {...rest} />;
});

interface ImportDialogSpaceContainerProps extends ComponentPropsWithoutRef<'div'> {}

const SpaceContainer = (props: ImportDialogSpaceContainerProps) => {
    const { children, className, ...rest } = props;

    return (
        <div
            className={twMerge('h-fit flex-grow rounded-lg border border-gray-200', className)}
            {...rest}
        >
            {children}
        </div>
    );
};

const Title = forwardRef((props: Dialog.DialogTitleProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { className, ...rest } = props;

    return (
        <Dialog.Title ref={ref} className={twMerge('text-xl font-medium', className)} {...rest} />
    );
});

export { Body, Content, Draft, Header, HelpCenter, SpaceContainer, Title };
